import plangebiedGeoJson from './plangebied.json';

/**
 * Het projectgebied, aangeleverd door de gemeente als GeoPackage en omgezet
 * met scripts/plangebied-uit-gpkg.mts.
 *
 * Alles in de app hangt hieraan: welke woningen op de kaart komen, wat het
 * totaal op het dashboard is, en of een handmatig punt binnen het project valt.
 */

export interface Omhullende {
  zuid: number;
  west: number;
  noord: number;
  oost: number;
}

type Ring = [number, number][];

/** Buitenring van elk vlak, in GeoJSON-volgorde (lengtegraad, breedtegraad). */
function haalRingen(): Ring[] {
  const geometrie = plangebiedGeoJson.geometry;

  if (geometrie.type === 'Polygon') {
    return (geometrie.coordinates as number[][][]).map((ring) => ring as Ring);
  }

  // MultiPolygon: per vlak alleen de buitenring; gaten komen in de praktijk
  // niet voor in een plangebied en zouden de toets alleen maar vertroebelen.
  return (geometrie.coordinates as unknown as number[][][][]).map(
    (vlak) => vlak[0] as Ring
  );
}

const RINGEN = haalRingen();

export const PLANGEBIED = plangebiedGeoJson;

export const PLANGEBIED_NAAM: string = plangebiedGeoJson.properties.naam;

/** Ringen als [breedtegraad, lengtegraad] — de volgorde die Leaflet wil. */
export const PLANGEBIED_LATLNGS: [number, number][][] = RINGEN.map((ring) =>
  ring.map(([lon, lat]) => [lat, lon] as [number, number])
);

export const PLANGEBIED_BBOX: Omhullende = (() => {
  const punten = RINGEN.flat();
  const lons = punten.map(([lon]) => lon);
  const lats = punten.map(([, lat]) => lat);
  return {
    west: Math.min(...lons),
    oost: Math.max(...lons),
    zuid: Math.min(...lats),
    noord: Math.max(...lats),
  };
})();

/**
 * Ligt dit punt in het plangebied?
 *
 * Ray casting: trek een denkbeeldige lijn naar rechts en tel hoe vaak die de
 * rand kruist. Oneven betekent binnen. Simpel, snel genoeg voor duizenden
 * punten, en zonder afhankelijkheden.
 */
export function ligtInPlangebied(latitude: number, longitude: number): boolean {
  // Snelle voorselectie op de omhullende rechthoek scheelt het meeste werk.
  if (
    latitude < PLANGEBIED_BBOX.zuid ||
    latitude > PLANGEBIED_BBOX.noord ||
    longitude < PLANGEBIED_BBOX.west ||
    longitude > PLANGEBIED_BBOX.oost
  ) {
    return false;
  }

  return RINGEN.some((ring) => {
    let binnen = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      const [lonI, latI] = ring[i];
      const [lonJ, latJ] = ring[j];

      const kruist = latI > latitude !== latJ > latitude;
      if (kruist && longitude < ((lonJ - lonI) * (latitude - latI)) / (latJ - latI) + lonI) {
        binnen = !binnen;
      }
    }
    return binnen;
  });
}

/** Middelpunt van de omhullende rechthoek; startpositie van de kaart. */
export const PLANGEBIED_MIDDEN: [number, number] = [
  (PLANGEBIED_BBOX.zuid + PLANGEBIED_BBOX.noord) / 2,
  (PLANGEBIED_BBOX.west + PLANGEBIED_BBOX.oost) / 2,
];
